/**
 * IletisimKisileriPage.jsx — Sistem Ayarları › İletişim Kişileri (backlog #538, backend #537, 2026-10-21)
 * Route: /Management/SistemAyarlari/IletisimKisileri   İzin: page.sistem (okuma personel; yazma iletisim_kisisi.manage)
 *
 * Firma portalının Ana Sayfa'sındaki "İletişim" widget'ının satırları: firmanın ulaşabileceği SAKA tarafı kişiler (atanmış
 * danışman, teknik destek hattı...). Bir satırın kapsamı: GENEL (her fuardaki her firma), FUAR (o fuardaki her firma) ya da
 * FIRMA (tek firma; isteğe bağlı olarak yalnızca bir fuarda). Portal üçünü birleştirip gösterir: önce firmanın kendi
 * satırları, sonra fuarınkiler, sonra genel olanlar; tabloda da aynı sırada dizilir. Başlık (rol) Türkçe + İngilizce tutulur,
 * ad / telefon / e-posta tek metindir. Silmek yerine portaldan gizlenebilir.
 * Servis: services/iletisimKisisiService.js (düz kaynak /api/v1/iletisim-kisileri).
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Card, CardBody, Table, Button, Badge, Alert, Spinner, Modal, ModalHeader, ModalBody, ModalFooter,
  Label, Input, Row, Col, FormGroup,
} from "reactstrap";
import { Pencil, Eye, EyeOff, Trash2, Plus, Phone, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import FirmaSearchSelect from "../../../Components/Firma/FirmaSearchSelect";
import i18n from "../../../i18n";
import { useYetki, PERMISSION_KEYS } from "../../../services/rbacService";
import { fuarService } from "../../../services/fuarService";
import {
  listIletisimKisileri, createIletisimKisisi, updateIletisimKisisi, setIletisimKisisiAktif, deleteIletisimKisisi,
  KAPSAM, ALAN_SINIRI, EPOSTA_DESENI,
} from "../../../services/iletisimKisisiService";

/** Portalın gösterdiği sıra: firmanın kendi satırları, fuarınkiler, genel olanlar; her biri sıra'ya göre. */
const KAPSAM_SIRASI = { [KAPSAM.FIRMA]: 0, [KAPSAM.FUAR]: 1, [KAPSAM.GENEL]: 2 };
const sirala = (a, b) => (KAPSAM_SIRASI[a.kapsam] - KAPSAM_SIRASI[b.kapsam]) || (a.sira - b.sira) || (a.id - b.id);

const KAPSAM_KISA = {
  [KAPSAM.GENEL]: "iletisimKisileri.kapsamGenelKisa",
  [KAPSAM.FUAR]: "iletisimKisileri.kapsamFuarKisa",
  [KAPSAM.FIRMA]: "iletisimKisileri.kapsamFirmaKisa",
};

const BOS_FORM = {
  kapsam: KAPSAM.GENEL, fuarId: "", firma: null, rolTr: "", rolEn: "", adSoyad: "", telefon: "", eposta: "", sira: "", aktif: true,
};

function formdan(satir) {
  if (!satir) return BOS_FORM;
  return {
    kapsam: satir.kapsam,
    fuarId: satir.fuarId != null ? String(satir.fuarId) : "",
    firma: satir.firmaId != null ? { firmaId: satir.firmaId, firmaName: satir.firmaAdi } : null,
    rolTr: satir.rolTr || "",
    rolEn: satir.rolEn || "",
    adSoyad: satir.adSoyad || "",
    telefon: satir.telefon || "",
    eposta: satir.eposta || "",
    sira: satir.sira ? String(satir.sira) : "",
    aktif: satir.aktif,
  };
}

// ─── Ekle / düzenle ─────────────────────────────────────────────────────────
function KisiModal({ satir, fuarlar, kapat, onKaydedildi }) {
  const { t } = useTranslation("ayarlar");
  const yeni = !satir;
  const [f, setF] = useState(() => formdan(satir));
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [denendi, setDenendi] = useState(false);
  const [hata, setHata] = useState(null);
  const ayarla = (k, v) => setF((o) => ({ ...o, [k]: v }));

  // Backend ile aynı kurallar; kayıt reddedilirse mesajı yine sunucudan gelir.
  const hatalar = {};
  if (!f.adSoyad.trim()) hatalar.adSoyad = t("iletisimKisileri.adGerekli");
  if (!f.telefon.trim() && !f.eposta.trim()) hatalar.iletisim = t("iletisimKisileri.telefonVeyaEposta");
  if (f.eposta.trim() && !EPOSTA_DESENI.test(f.eposta.trim())) hatalar.eposta = t("iletisimKisileri.epostaGecersiz");
  if (f.rolEn.trim() && !f.rolTr.trim()) hatalar.rolEn = t("iletisimKisileri.ingilizceBaslikTurkcesiz");
  if (f.kapsam === KAPSAM.FUAR && !f.fuarId) hatalar.fuar = t("iletisimKisileri.fuarSecin");
  if (f.kapsam === KAPSAM.FIRMA && !f.firma) hatalar.firma = t("iletisimKisileri.firmaSecin");
  const goster = (k) => (denendi ? hatalar[k] : null);

  async function kaydet() {
    if (Object.keys(hatalar).length) { setDenendi(true); return; }
    setGonderiliyor(true);
    setHata(null);
    const govde = { ...f, firmaId: f.firma?.firmaId ?? null };
    try {
      if (yeni) await createIletisimKisisi(govde); else await updateIletisimKisisi(satir.id, govde);
      onKaydedildi(yeni ? t("iletisimKisileri.eklendi") : t("iletisimKisileri.guncellendi"));
    } catch (e) {
      setHata(e.message);
      setGonderiliyor(false);
    }
  }

  const fuarSecici = (id, bos) => (
    <Input id={id} type="select" bsSize="sm" value={f.fuarId} invalid={!!goster("fuar")} onChange={(e) => ayarla("fuarId", e.target.value)}>
      <option value="">{bos}</option>
      {fuarlar.map((x) => <option key={x.fuarId} value={x.fuarId}>{x.fuarName || x.fuarShortName || `#${x.fuarId}`}</option>)}
    </Input>
  );

  return (
    <Modal isOpen toggle={() => !gonderiliyor && kapat()} centered size="lg">
      <ModalHeader toggle={() => !gonderiliyor && kapat()}>
        {yeni ? t("iletisimKisileri.yeniKisiBaslik") : t("iletisimKisileri.duzenleBaslik")}
      </ModalHeader>
      <ModalBody>
        <Row className="g-2">
          <Col md={6}>
            <FormGroup>
              <Label for="ik-kapsam" className="small mb-1">{t("iletisimKisileri.kapsam")}</Label>
              <Input id="ik-kapsam" type="select" bsSize="sm" value={f.kapsam} onChange={(e) => ayarla("kapsam", e.target.value)}>
                <option value={KAPSAM.GENEL}>{t("iletisimKisileri.kapsamGenel")}</option>
                <option value={KAPSAM.FUAR}>{t("iletisimKisileri.kapsamFuar")}</option>
                <option value={KAPSAM.FIRMA}>{t("iletisimKisileri.kapsamFirma")}</option>
              </Input>
            </FormGroup>
          </Col>
          {f.kapsam === KAPSAM.FUAR && (
            <Col md={6}>
              <FormGroup>
                <Label for="ik-fuar" className="small mb-1">{t("iletisimKisileri.fuar")}</Label>
                {fuarSecici("ik-fuar", t("iletisimKisileri.secin"))}
                {goster("fuar") && <div className="small text-danger mt-1">{hatalar.fuar}</div>}
              </FormGroup>
            </Col>
          )}
          {f.kapsam === KAPSAM.FIRMA && (
            <>
              <Col md={6}>
                <FormGroup>
                  <Label className="small mb-1">{t("iletisimKisileri.firma")}</Label>
                  <FirmaSearchSelect value={f.firma} onChange={(x) => ayarla("firma", x)} forceLive invalid={!!goster("firma")} />
                  {goster("firma") && <div className="small text-danger mt-1">{hatalar.firma}</div>}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="ik-fuar" className="small mb-1">{t("iletisimKisileri.firmaFuarIsteBagli")}</Label>
                  {fuarSecici("ik-fuar", t("iletisimKisileri.tumFuarlar"))}
                </FormGroup>
              </Col>
            </>
          )}
        </Row>
        <Row className="g-2">
          <Col md={6}>
            <FormGroup>
              <Label for="ik-rol-tr" className="small mb-1">{t("iletisimKisileri.baslikTr")}</Label>
              <Input id="ik-rol-tr" bsSize="sm" value={f.rolTr} maxLength={ALAN_SINIRI.rol} placeholder={t("iletisimKisileri.baslikYer")}
                onChange={(e) => ayarla("rolTr", e.target.value)} />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="ik-rol-en" className="small mb-1">{t("iletisimKisileri.baslikEn")}</Label>
              <Input id="ik-rol-en" bsSize="sm" value={f.rolEn} maxLength={ALAN_SINIRI.rol} invalid={!!goster("rolEn")}
                onChange={(e) => ayarla("rolEn", e.target.value)} />
              {goster("rolEn") && <div className="small text-danger mt-1">{hatalar.rolEn}</div>}
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup>
              <Label for="ik-ad" className="small mb-1">{t("iletisimKisileri.adSoyad")}</Label>
              <Input id="ik-ad" bsSize="sm" value={f.adSoyad} maxLength={ALAN_SINIRI.adSoyad} invalid={!!goster("adSoyad")}
                onChange={(e) => ayarla("adSoyad", e.target.value)} />
              {goster("adSoyad") && <div className="small text-danger mt-1">{hatalar.adSoyad}</div>}
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="ik-tel" className="small mb-1">{t("iletisimKisileri.telefon")}</Label>
              <Input id="ik-tel" bsSize="sm" value={f.telefon} maxLength={ALAN_SINIRI.telefon} invalid={!!goster("iletisim")}
                onChange={(e) => ayarla("telefon", e.target.value)} />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="ik-eposta" className="small mb-1">{t("iletisimKisileri.eposta")}</Label>
              <Input id="ik-eposta" bsSize="sm" value={f.eposta} maxLength={ALAN_SINIRI.eposta} invalid={!!(goster("eposta") || goster("iletisim"))}
                onChange={(e) => ayarla("eposta", e.target.value)} />
              {(goster("eposta") || goster("iletisim")) && <div className="small text-danger mt-1">{hatalar.eposta || hatalar.iletisim}</div>}
            </FormGroup>
          </Col>
          <Col xs={5} md={3}>
            <FormGroup>
              <Label for="ik-sira" className="small mb-1">{t("iletisimKisileri.sira")}</Label>
              <Input id="ik-sira" type="number" bsSize="sm" value={f.sira} onChange={(e) => ayarla("sira", e.target.value)} />
            </FormGroup>
          </Col>
          <Col xs={7} md={9} className="d-flex align-items-center pt-3">
            <div className="form-check mb-0">
              <Input id="ik-aktif" type="checkbox" className="form-check-input" checked={f.aktif} onChange={(e) => ayarla("aktif", e.target.checked)} />
              <Label for="ik-aktif" className="form-check-label small">{t("iletisimKisileri.portaldaGoster")}</Label>
            </div>
          </Col>
        </Row>
        {hata && <Alert color="danger" className="py-2 small mt-2 mb-0">{hata}</Alert>}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={kapat} disabled={gonderiliyor}>{t("iletisimKisileri.vazgec")}</Button>
        <Button color="primary" onClick={kaydet} disabled={gonderiliyor} data-testid="ik-kaydet">
          {gonderiliyor && <Spinner size="sm" className="me-1" />}{t("iletisimKisileri.kaydet")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Silme onayı ────────────────────────────────────────────────────────────
function SilModal({ satir, kapat, onSilindi }) {
  const { t } = useTranslation("ayarlar");
  const [siliniyor, setSiliniyor] = useState(false);
  const [hata, setHata] = useState(null);

  async function sil() {
    setSiliniyor(true);
    setHata(null);
    try {
      await deleteIletisimKisisi(satir.id);
      onSilindi(t("iletisimKisileri.silindi"));
    } catch (e) {
      setHata(e.message);
      setSiliniyor(false);
    }
  }

  return (
    <Modal isOpen toggle={() => !siliniyor && kapat()} centered>
      <ModalHeader toggle={() => !siliniyor && kapat()}>{t("iletisimKisileri.silBaslik")}</ModalHeader>
      <ModalBody>
        <div className="small">{t("iletisimKisileri.silAciklama", { ad: satir.adSoyad })}</div>
        {hata && <Alert color="danger" className="py-2 small mt-3 mb-0">{hata}</Alert>}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={kapat} disabled={siliniyor}>{t("iletisimKisileri.vazgec")}</Button>
        <Button color="danger" onClick={sil} disabled={siliniyor} data-testid="ik-sil-onay">
          {siliniyor && <Spinner size="sm" className="me-1" />}{t("iletisimKisileri.sil")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Sayfa ──────────────────────────────────────────────────────────────────
export default function IletisimKisileriPage() {
  const { t } = useTranslation("ayarlar");
  const yazabilir = useYetki(PERMISSION_KEYS.ILETISIM_KISISI_MANAGE) === true;
  const [kisiler, setKisiler] = useState(null);
  const [yuklemeHatasi, setYuklemeHatasi] = useState(null);
  const [fuarlar, setFuarlar] = useState([]);
  const [yenile, setYenile] = useState(0);
  const [filtre, setFiltre] = useState({ ara: "", kapsam: "", fuarId: "" });
  const [modal, setModal] = useState(null);       // null | { satir: null (yeni) | satır }
  const [silinecek, setSilinecek] = useState(null);
  const [mesaj, setMesaj] = useState(null);
  const [islemdeki, setIslemdeki] = useState(null);
  const [islemHatasi, setIslemHatasi] = useState(null);

  useEffect(() => { document.title = i18n.t("ayarlar:iletisimKisileri.sayfaBasligi"); }, []);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const list = await listIletisimKisileri();
        if (!ignore) { setKisiler(list.sort(sirala)); setYuklemeHatasi(null); }
      } catch (e) {
        if (!ignore) { setYuklemeHatasi(e.message); setKisiler([]); }
      }
    }
    run();
    return () => { ignore = true; };
  }, [yenile]);

  // Fuar adları yalnızca seçici ve süzgeç için; okunamazsa satırların kendi fuarAdi'si yeter.
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const list = await fuarService.getFuarList();
        if (!ignore) setFuarlar(list);
      } catch {
        if (!ignore) setFuarlar([]);
      }
    }
    run();
    return () => { ignore = true; };
  }, []);

  const gorunen = useMemo(() => {
    const ara = filtre.ara.trim().toLowerCase();
    return (kisiler || []).filter((k) =>
      (!filtre.kapsam || k.kapsam === filtre.kapsam)
      && (!filtre.fuarId || String(k.fuarId) === filtre.fuarId)
      && (!ara || [k.adSoyad, k.rolTr, k.rolEn, k.telefon, k.eposta, k.firmaAdi, k.fuarAdi]
        .some((v) => v && String(v).toLowerCase().includes(ara))));
  }, [kisiler, filtre]);

  function bitti(metin) {
    setModal(null);
    setSilinecek(null);
    setMesaj(metin);
    setYenile((n) => n + 1);
  }

  async function gizleGoster(k) {
    setIslemdeki(k.id);
    setIslemHatasi(null);
    try {
      await setIletisimKisisiAktif(k.id, !k.aktif);
      setYenile((n) => n + 1);
    } catch (e) {
      setIslemHatasi(e.message);
    } finally {
      setIslemdeki(null);
    }
  }

  const yetkiIpucu = yazabilir ? undefined : t("iletisimKisileri.yazmaYetkisi");

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("iletisimKisileri.baslik")} pageTitle={t("iletisimKisileri.sistemAyarlari")} />

        <Card className="mb-3">
          <CardBody className="py-3">
            <Row className="g-2 align-items-end">
              <Col md={4} lg={3}>
                <Label for="ik-ara" className="small mb-1">{t("iletisimKisileri.ara")}</Label>
                <Input id="ik-ara" bsSize="sm" value={filtre.ara} placeholder={t("iletisimKisileri.araYer")}
                  onChange={(e) => setFiltre((f) => ({ ...f, ara: e.target.value }))} />
              </Col>
              <Col xs={6} md={3} lg={2}>
                <Label for="ik-f-kapsam" className="small mb-1">{t("iletisimKisileri.kapsam")}</Label>
                <Input id="ik-f-kapsam" type="select" bsSize="sm" value={filtre.kapsam} onChange={(e) => setFiltre((f) => ({ ...f, kapsam: e.target.value }))}>
                  <option value="">{t("common:tumu")}</option>
                  <option value={KAPSAM.GENEL}>{t("iletisimKisileri.kapsamGenelKisa")}</option>
                  <option value={KAPSAM.FUAR}>{t("iletisimKisileri.kapsamFuarKisa")}</option>
                  <option value={KAPSAM.FIRMA}>{t("iletisimKisileri.kapsamFirmaKisa")}</option>
                </Input>
              </Col>
              <Col xs={6} md={3} lg={3}>
                <Label for="ik-f-fuar" className="small mb-1">{t("iletisimKisileri.fuar")}</Label>
                <Input id="ik-f-fuar" type="select" bsSize="sm" value={filtre.fuarId} onChange={(e) => setFiltre((f) => ({ ...f, fuarId: e.target.value }))}>
                  <option value="">{t("iletisimKisileri.tumFuarlar")}</option>
                  {fuarlar.map((x) => <option key={x.fuarId} value={x.fuarId}>{x.fuarName || x.fuarShortName || `#${x.fuarId}`}</option>)}
                </Input>
              </Col>
              <Col xs="auto" className="ms-auto">
                {/* Kapalı düğme fare olaylarını yutar: ipucu sarmalayıcıda durur. */}
                <span title={yetkiIpucu}>
                  <Button color="primary" size="sm" disabled={!yazabilir} onClick={() => { setMesaj(null); setModal({ satir: null }); }} data-testid="yeni-kisi">
                    <Plus size={14} className="me-1" />{t("iletisimKisileri.yeniKisi")}
                  </Button>
                </span>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {mesaj && <Alert color="success" toggle={() => setMesaj(null)} className="py-2 small">{mesaj}</Alert>}
        {islemHatasi && <Alert color="danger" toggle={() => setIslemHatasi(null)} className="py-2 small">{islemHatasi}</Alert>}
        {yuklemeHatasi && <Alert color="danger">{yuklemeHatasi}</Alert>}

        {kisiler == null ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : gorunen.length === 0 ? (
          !yuklemeHatasi && (
            <div className="text-muted">{kisiler.length === 0 ? t("iletisimKisileri.henuzYok") : t("iletisimKisileri.suzgeceUyanYok")}</div>
          )
        ) : (
          <Card>
            <CardBody className="p-0">
              <div className="table-responsive">
                <Table className="align-middle mb-0" size="sm" hover>
                  <thead className="text-muted small">
                    <tr>
                      <th className="ps-3">{t("iletisimKisileri.kapsam")}</th>
                      <th>{t("iletisimKisileri.baslikKolon")}</th>
                      <th>{t("iletisimKisileri.kisi")}</th>
                      <th className="text-end">{t("iletisimKisileri.sira")}</th>
                      <th>{t("iletisimKisileri.durum")}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {gorunen.map((k) => (
                      <tr key={k.id} className={k.aktif ? "" : "text-muted"} data-testid={`satir-${k.id}`}>
                        <td className="ps-3">
                          <Badge color="light" className="text-body border fw-normal">
                            {t(KAPSAM_KISA[k.kapsam])}
                          </Badge>
                          {k.firmaId != null && <div className="small mt-1">{k.firmaAdi || `#${k.firmaId}`}</div>}
                          {k.fuarId != null && <div className="small text-muted">{k.fuarAdi || `#${k.fuarId}`}</div>}
                        </td>
                        <td>
                          <div className="small">{k.rolTr || "—"}</div>
                          {k.rolEn && <div className="small text-muted">{k.rolEn}</div>}
                        </td>
                        <td>
                          <div className="fw-semibold small">{k.adSoyad}</div>
                          {k.telefon && <div className="small text-muted d-flex align-items-center gap-1"><Phone size={12} />{k.telefon}</div>}
                          {k.eposta && <div className="small text-muted d-flex align-items-center gap-1"><Mail size={12} />{k.eposta}</div>}
                        </td>
                        <td className="text-end">{k.sira}</td>
                        <td className="text-nowrap small">{k.aktif ? t("iletisimKisileri.gorunur") : t("iletisimKisileri.gizli")}</td>
                        <td className="text-end pe-3 text-nowrap">
                          <span title={yetkiIpucu}>
                            <Button color="link" size="sm" className="p-1" disabled={!yazabilir} title={t("common:duzenle")}
                              onClick={() => { setMesaj(null); setModal({ satir: k }); }} data-testid={`duzenle-${k.id}`}>
                              <Pencil size={16} />
                            </Button>
                            <Button color="link" size="sm" className="p-1" disabled={!yazabilir || islemdeki === k.id}
                              title={k.aktif ? t("iletisimKisileri.portaldanGizle") : t("iletisimKisileri.portaldaYenidenGoster")}
                              onClick={() => gizleGoster(k)} data-testid={`gizle-${k.id}`}>
                              {islemdeki === k.id ? <Spinner size="sm" /> : k.aktif ? <EyeOff size={16} /> : <Eye size={16} />}
                            </Button>
                            <Button color="link" size="sm" className="p-1 text-danger" disabled={!yazabilir} title={t("common:sil")}
                              onClick={() => { setMesaj(null); setSilinecek(k); }} data-testid={`sil-${k.id}`}>
                              <Trash2 size={16} />
                            </Button>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        )}

        {modal && (
          <KisiModal satir={modal.satir} fuarlar={fuarlar} kapat={() => setModal(null)} onKaydedildi={bitti} />
        )}
        {silinecek && <SilModal satir={silinecek} kapat={() => setSilinecek(null)} onSilindi={bitti} />}
      </Container>
    </div>
  );
}
